import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

function staggerStyle(i: number): React.CSSProperties {
  return { animationDelay: `${80 + i * 60}ms` };
}

function IconCalendar({ className = 'h-16 w-16' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.75}
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );
}

export default function ReportsPage() {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  const complianceFramework =
    userProfile?.compliance_framework?.trim() || 'your applicable regulatory framework';

  if (!currentUser?.id) {
    return (
      <div className="mx-auto max-w-4xl py-12 text-center text-muted">
        <p>Sign in to view your security reports.</p>
      </div>
    );
  }

  const pdfTooltip = 'Available after API integrations are complete';

  return (
    <div className="mx-auto max-w-6xl space-y-8 pb-4 sm:space-y-10 sm:pb-6">
      <header className="opacity-0 animate-fade-in-up space-y-3" style={staggerStyle(0)}>
        <h2 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">Security Reports</h2>
        <p className="max-w-3xl text-sm leading-relaxed text-muted sm:text-base">
          Monthly summaries of your business security posture — ready to share with your team, bank, or regulator.
        </p>
      </header>

      <section
        className="grid grid-cols-2 gap-3 opacity-0 animate-fade-in-up lg:grid-cols-4"
        style={staggerStyle(1)}
      >
        {[
          { label: 'Total Reports', value: '1', hint: 'In your archive' },
          { label: 'Last Generated', value: 'This month', hint: 'April 2026' },
          { label: 'Compliance Framework', value: complianceFramework, hint: 'Your profile' },
          { label: 'Next Report', value: 'In 28 days', hint: 'May 1, 2026' },
        ].map((card) => (
          <div
            key={card.label}
            className="rounded-xl border border-border bg-surface/60 p-4 sm:p-5"
          >
            <p className="text-xs font-medium uppercase tracking-wide text-muted">{card.label}</p>
            <p className="mt-2 line-clamp-2 text-lg font-bold text-foreground sm:text-xl">{card.value}</p>
            <p className="mt-1 text-xs text-muted">{card.hint}</p>
          </div>
        ))}
      </section>

      <section className="opacity-0 animate-fade-in-up" style={staggerStyle(2)}>
        <div className="rounded-2xl border border-border bg-surface/70 p-5 sm:p-6 lg:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 space-y-2">
              <span className="inline-flex w-fit rounded-full border border-accent/40 bg-accent/15 px-3 py-1 text-xs font-semibold text-accent">
                Latest Report
              </span>
              <h3 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
                April 2026 Security Summary
              </h3>
              <p className="text-sm text-muted sm:text-base">Generated on April 1, 2026</p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-border bg-navy/40 px-4 py-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted">Threats Blocked</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">5</p>
            </div>
            <div className="rounded-xl border border-border bg-navy/40 px-4 py-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted">Dark Web Status</p>
              <p className="mt-1 text-2xl font-bold text-accent">Clear</p>
            </div>
            <div className="rounded-xl border border-border bg-navy/40 px-4 py-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted">Overall Score</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">72/100</p>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <span className="w-full sm:w-auto" title={pdfTooltip}>
              <button
                type="button"
                disabled
                aria-label={`Download PDF. ${pdfTooltip}`}
                className="w-full cursor-not-allowed rounded-lg border border-border bg-surface/30 px-5 py-3 text-sm font-semibold text-foreground opacity-50 disabled:pointer-events-none"
              >
                Download PDF
              </button>
            </span>
            <button
              type="button"
              onClick={() => navigate('/reports/april-2026')}
              className="w-full rounded-lg bg-accent px-5 py-3 text-sm font-semibold text-cta-text transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface sm:w-auto"
            >
              View Full Report
            </button>
          </div>
        </div>
      </section>

      <section className="opacity-0 animate-fade-in-up space-y-4" style={staggerStyle(3)}>
        <h3 className="text-lg font-semibold text-foreground sm:text-xl">Previous Reports</h3>
        <div className="flex flex-col items-center justify-center rounded-2xl border border-border bg-surface/40 px-6 py-14 text-center sm:py-16">
          <IconCalendar className="h-14 w-14 text-accent/80 sm:h-16 sm:w-16" />
          <p className="mt-5 text-base font-semibold text-foreground sm:text-lg">No previous reports yet</p>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-muted sm:text-base">
            Reports are generated automatically on the first of each month. Your next report will be ready on May 1,
            2026.
          </p>
        </div>
      </section>

      <aside
        className="opacity-0 animate-fade-in-up rounded-xl border border-warning/40 bg-warning/10 px-4 py-4 sm:px-5 sm:py-5"
        style={staggerStyle(4)}
      >
        <p className="text-sm leading-relaxed text-foreground sm:text-base">
          Automated report generation and PDF downloads will be available once API integrations are complete. Your data
          is already being tracked and will populate automatically.
        </p>
      </aside>
    </div>
  );
}
